import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import routes from '../../../utils/constants/routes';
import colors from '../../../styled/colors';
import useIsSafari from '../../../hooks/useIsSafari';
import { DoggoLogo, FullLogo } from '../../../assets/logos/logoRepository';
import {
  InstagramLetterIcon,
  InstagramStoriesIcon,
  InstagramColorIcon,
} from '../../../assets/icons/iconRepository';
import { Button } from '../../../components/buttons';
import mediaQueries from '../../../utils/mediaQueries';

const Section = styled.section`
  background: linear-gradient(
    83.94deg,
    ${colors.orangeInstagram} -26.52%,
    ${colors.pinkInstagram} 54.11%,
    ${colors.blueInstagram} 144.72%
  );
  padding-top: 32px;
  padding-bottom: 40px;
  @media ${mediaQueries.mdUp} {
    background: radial-gradient(
      172.17% 574.34% at -36.43% 127.04%,
      ${colors.yellowInstagram} 0%,
      ${colors.orangeInstagram} 20.84%,
      ${colors.pinkInstagram} 68.07%,
      ${colors.blueInstagram}
    );
    display: grid;
    grid-template-areas:
      'intro actions'
      'info actions'
      'cta actions';
    grid-template-rows: max-content;
    padding: 28px 38px;
  }
  @media ${mediaQueries.lgPlusUp} {
    padding: 56px 51px 38px;
  }
  @media ${mediaQueries.xlPlusUp} {
    background: linear-gradient(
      83.94deg,
      ${colors.orangeInstagram} -26.52%,
      ${colors.pinkInstagram} 54.11%,
      ${colors.blueInstagram} 144.71%
    );
    padding: 34px 113px;
  }
`;
const Intro = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  @media ${mediaQueries.mdUp} {
    grid-area: intro;
    justify-content: flex-start;
    height: fit-content;
    margin-top: 15px;
  }
  @media ${mediaQueries.lgPlusUp} {
    margin-top: 20px;
  }
`;
const IntroContainer = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
`;
const Logo = styled.div`
  background: ${colors.white};
  border-radius: 4px 0 0 4px;
  display: flex;
  justify-content: center;
  height: fit-content;
  width: max-content;
  padding: 5px 6px;
  @media ${mediaQueries.mdUp} {
    padding: 6px;
  }
  @media ${mediaQueries.lgPlusUp} {
    padding: 8px 6px;
  }
  @media ${mediaQueries.xlPlusUp} {
    padding: 5px;
  }
`;
const LogoContainer = styled.div`
  display: flex;
  width: 90px;
  @media ${mediaQueries.mdUp} {
    width: 60px;
  }
  @media ${mediaQueries.lgPlusUp} {
    width: 74px;
  }
  @media ${mediaQueries.xlPlusUp} {
    width: 90px;
  }
`;
const LogoIcon = styled.img`
  object-fit: contain;
  width: 100%;
`;
const LogoText = styled.span`
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
  @media ${mediaQueries.mdUp} {
    font-size: 8px;
    margin-left: 6px;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 10px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 12px;
  }
`;
const InstagramContainer = styled.div`
  background: ${colors.pink};
  height: fit-content;
  padding: 14px 7px 8px;
  @media ${mediaQueries.mdUp} {
    padding: 9px 7px 6px;
  }
  @media ${mediaQueries.lgPlusUp} {
    padding: 10px 17px;
  }
  @media ${mediaQueries.xlPlusUp} {
    padding: 14px 13px 7px;
  }
`;
const LetterLogoContainer = styled.div`
  display: flex;
  width: 94px;
  @media ${mediaQueries.mdUp} {
    width: 53px;
  }
  @media ${mediaQueries.lgPlusUp} {
    width: 71px;
  }
  @media ${mediaQueries.xlPlusUp} {
    width: 94px;
  }
`;
const LetterLogo = styled.img`
  width: 100%;
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-top: 20px;
  @media (min-width: 400px) {
    position: relative;
    left: 12px;
  }
  @media ${mediaQueries.mdUp} {
    grid-area: actions;
    margin-top: 0;
    justify-content: space-between;
    align-items: flex-start;
    left: 0;
  }
`;
const ActionImageContainer = styled.div`
  transform: rotate(5.99deg);
  position: relative;
  left: -12px;
  width: 182px;
  @media ${mediaQueries.mdUp} {
    left: 0;
    width: 170px;
  }
  @media ${mediaQueries.lgPlusUp} {
    width: 221px;
  }
  @media ${mediaQueries.xlPlusUp} {
    width: 318px;
  }
`;
const ActionImage = styled.img`
  width: 100%;
`;
const Video = styled.video`
  transform: rotate(5.99deg);
  position: relative;
  left: -12px;
  width: 182px;
  @media ${mediaQueries.mdUp} {
    left: 0;
    width: 170px;
  }
  @media ${mediaQueries.lgPlusUp} {
    width: 221px;
  }
  @media ${mediaQueries.xlPlusUp} {
    width: 318px;
  }
`;
const Actions = styled.div`
  margin-left: 28px;
  margin-right: 18px;
  @media (min-width: 400px) {
    margin-right: 0;
  }
  @media ${mediaQueries.mdUp} {
    margin-top: 36px;
    margin-left: 24px;
  }
  @media ${mediaQueries.lgPlusUp} {
    margin-left: 30px;
    margin-top: 24px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-left: 56px;
    margin-top: 54px;
  }
`;
const ActionsContainer = styled.div`
  margin-bottom: 12px;
  @media ${mediaQueries.mdUp} {
    margin-bottom: 0;
  }
`;
const Action = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.white};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  width: max-content;
  border-radius: 30px;
  padding: 8px;
  @media ${mediaQueries.mdUp} {
    padding: 7px 8px 6px;
  }
  @media ${mediaQueries.lgPlusUp} {
    padding: 11px 16px;
  }
  &:not(:last-child) {
    margin-bottom: 8px;
    @media ${mediaQueries.mdUp} {
      margin-bottom: 18px;
    }
    @media ${mediaQueries.lgPlusUp} {
      margin-bottom: 20px;
    }
    @media ${mediaQueries.xlPlusUp} {
      margin-bottom: 27px;
    }
  }
`;
const IconContainer = styled.div`
  display: flex;
  width: max-content;
  height: 17px;
  @media ${mediaQueries.lgPlusUp} {
    height: 22px;
  }
  @media ${mediaQueries.xlPlusUp} {
    height: 24px;
  }
`;
const Icon = styled.img`
  height: 100%;
`;
const ActionText = styled.span`
  color: ${colors.grayMediaLibrary};
  display: block;
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
  max-width: ${({ maxWidth }) => maxWidth};
  margin-left: 6px;
  @media ${mediaQueries.mdUp} {
    max-width: max-content;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 13px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 16px;
  }
`;
const TextContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  margin-top: 30px;
  @media ${mediaQueries.mdUp} {
    grid-area: info;
    justify-items: start;
    margin-top: 4px;
    padding-right: 20px;
  }
  @media ${mediaQueries.lgPlusUp} {
    margin-top: 14px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 12px;
  }
`;
const Title = styled.h1`
  color: ${colors.white};
  font-size: 20px;
  font-weight: bold;
  line-height: 22px;
  text-align: center;
  max-width: 288px;
  @media ${mediaQueries.mdUp} {
    font-size: 22px;
    text-align: left;
    max-width: max-content;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 28px;
    line-height: 100%;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 40px;
    line-height: 45px;
  }
`;

const Subitle = styled.h3`
  color: ${colors.white};
  font-size: 14px;
  font-weight: normal;
  line-height: 19px;
  text-align: center;
  max-width: 288px;
  margin-top: 8px;
  @media ${mediaQueries.mdUp} {
    font-size: 11px;
    line-height: 107%;
    text-align: left;
    margin-top: 0;
    max-width: max-content;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 16px;
    margin-top: 10px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 20px;
    line-height: 27px;
    margin-top: 0;
  }
`;
const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 44px;
  @media ${mediaQueries.mdUp} {
    grid-area: cta;
    justify-content: flex-start;
    margin-top: 24px;
    height: fit-content;
  }
  @media ${mediaQueries.lgPlusUp} {
    margin-top: 40px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 24px;
  }
`;
const ButtonContainer = styled.div``;
const CustomButton = styled(Button)`
  && {
    background: ${colors.secondary};
    border-radius: 4px;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    color: ${colors.black3E};
    font-size: 16px;
    font-weight: 600;
    &:hover {
      background: ${colors.secondary};
      color: ${colors.black3E};
    }
    @media ${mediaQueries.mdUp} {
      font-size: 14px;
    }
    @media ${mediaQueries.lgPlusUp} {
      font-size: 18px;
    }
    @media ${mediaQueries.xlPlusUp} {
      font-size: 20px;
      padding: 15px 28px;
    }
  }
`;
const ButtonHelpText = styled.p`
  color: ${colors.white};
  font-size: 14px;
  font-weight: normal;
  text-align: center;
  margin: 0;
  width: 100%;
  margin-top: 16px;
  @media ${mediaQueries.mdUp} {
    font-size: 11px;
    line-height: 15px;
    text-align: left;
    margin-top: 8px;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 14px;
    line-height: 19px;
    margin-top: 10px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 14px;
  }
`;

const Header = ({ daysTrial }) => {
  const intl = useIntl();
  const history = useHistory();
  const { isSafari } = useIsSafari();
  const [dogtorVideoUrl, setDogtorVideoUrl] = useState(
    'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/dogtor-en.webm',
  );
  const [dogtorGIFUrl, setDogtorGIFUrl] = useState(
    'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/dogtor-en.gif',
  );

  useEffect(() => {
    if (intl.locale === 'es') {
      setDogtorVideoUrl(
        'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/dogtor-es.webm',
      );
      setDogtorGIFUrl(
        'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/dogtor-es.gif',
      );
    }
  }, [intl]);
  return (
    <Section>
      <Intro>
        <IntroContainer>
          <Logo>
            <LogoContainer>
              <LogoIcon src={FullLogo} alt="Social Pipe Logo" />
            </LogoContainer>
            <LogoText>
              <FormattedMessage
                id="landing.instagram.header.intro.text"
                defaultMessage="for"
              />
            </LogoText>
          </Logo>
          <InstagramContainer>
            <LetterLogoContainer>
              <LetterLogo
                src={InstagramLetterIcon}
                alt="Letter Icon Instagram"
              />
            </LetterLogoContainer>
          </InstagramContainer>
        </IntroContainer>
      </Intro>
      <ActionsSection>
        {isSafari ? (
          <ActionImageContainer>
            <ActionImage src={dogtorGIFUrl} />
          </ActionImageContainer>
        ) : (
          <Video autoPlay loop muted playsInline>
            <source src={dogtorVideoUrl} type="video/webm" />
            <ActionImage src={dogtorGIFUrl} />
            Your browser does not support HTML5 video.
          </Video>
        )}
        <Actions>
          <ActionsContainer>
            <Action>
              <IconContainer>
                <Icon src={InstagramStoriesIcon} alt="Instagram Stories icon" />
              </IconContainer>
              <ActionText maxWidth="58px">
                <FormattedMessage
                  id="landing.instagram.header.actions.action1"
                  defaultMessage="Schedule IG Stories"
                />
              </ActionText>
            </Action>
            <Action>
              <IconContainer>
                <Icon src={InstagramColorIcon} alt="Instagram icon" />
              </IconContainer>
              <ActionText maxWidth="85px">
                <FormattedMessage
                  id="landing.instagram.header.actions.action2"
                  defaultMessage="Publish image or video"
                />
              </ActionText>
            </Action>
            <Action>
              <IconContainer>
                <Icon src={DoggoLogo} alt="Piper icon" />
              </IconContainer>
              <ActionText maxWidth="93px">
                <FormattedMessage
                  id="landing.instagram.header.actions.action3"
                  defaultMessage="See the best times to post"
                />
              </ActionText>
            </Action>
          </ActionsContainer>
        </Actions>
      </ActionsSection>
      <TextContainer>
        <Title>
          <FormattedMessage
            id="landing.instagram.header.title"
            defaultMessage="Give superpowers to your Business Instagram"
          />
        </Title>
        <Subitle>
          <FormattedMessage
            id="landing.instagram.header.subtitle"
            defaultMessage="With Social Piper you can schedule your stories, posts, videos, or images to all of your business social media profiles."
          />
        </Subitle>
      </TextContainer>
      <CTAContainer>
        <ButtonContainer onClick={() => history.push(routes.PRICING)}>
          <CustomButton>
            <FormattedMessage
              id="landing.instagram.header.cta.button"
              defaultMessage="Start Your {daysTrial}-Day Trial"
              values={{
                daysTrial,
              }}
            />
          </CustomButton>
        </ButtonContainer>
        <ButtonHelpText>
          <FormattedMessage
            id="landing.instagram.header.cta.button.help.text"
            defaultMessage="Risk-free, you can cancel at any time"
          />
        </ButtonHelpText>
      </CTAContainer>
    </Section>
  );
};

Header.propTypes = {
  daysTrial: PropTypes.number.isRequired,
};

export default Header;
